import { TotalResults } from '@shared/components/ui/TotalResults';
import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useIsMobile } from '@shared/hooks/useMobile';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { EntryStatus } from '../../../models';
import { useEntriesCountByStatus } from '../../hooks/useEntriesCountByStatus';
import { useEntriesMain } from '../../hooks/useEntriesMain';
import { EntryAgGridTable } from '../display';
import { EntryCardList } from '../display/EntryCardList';
import { ErrorState, LoadingState } from '../feedback';
import { EntryStatusToolbar, FiltersSection, CancelEntryModal } from '../interaction';
import { Entry } from '../../../models';
import { useRef } from 'react';

const createHeaderActions = (
  handleCreateEntry: () => void,
  t: (key: string, defaultValue?: string) => string,
  canCreate: boolean,
) => {
  if (!canCreate) return [];
  return [
    {
      id: 'create-entry',
      label: t('buttons.create', 'Crear entrada'),
      onClick: handleCreateEntry,
      variant: 'primary' as const,
      icon: <Plus className="w-4 h-4" />,
    },
  ];
};

export const Main = () => {
  const { t } = useTranslation('merchandiseV3');
  const navigate = useNavigate();
  const isMobileViewport = useIsMobile();
  const { canAccess, teamType } = usePermissions();
  const cancelEntryModalRef = useRef<{ openWithEntry: (entry: Entry) => void }>(null);
  const isTenant3pl = teamType === 'tenant_3pl';

  // Verificar si el usuario puede crear entradas
  // Solo tenant_external puede crear mercancías según PERMISOS_SISTEMA.md
  const canCreate = canAccess(['tenant_external'], ['merchandises.create']);

  // Verificar si el usuario puede cancelar entradas
  // Solo tenant y tenant_3pl pueden cancelar entradas, NO tenant_external
  const canCancel = canAccess(['tenant', 'tenant_3pl'], ['merchandises.cancel']);

  // Handler para crear entrada
  const handleCreateEntry = useCallback(() => {
    navigate('/merchandise/create');
  }, [navigate]);

  // Handler para cancelar entrada
  const handleCancelEntry = useCallback(
    (entry: Entry) => {
      if (!canCancel) return;
      cancelEntryModalRef.current?.openWithEntry(entry);
    },
    [canCancel],
  );

  // Breadcrumbs y título de página
  const breadcrumbs = useMemo(() => [{ label: t('breadcrumb.merchandise', 'Mercancía') }], [t]);

  // Acciones del header - Solo si tiene permiso de crear
  const headerActions = useMemo(
    () => createHeaderActions(handleCreateEntry, t, canCreate),
    [handleCreateEntry, t, canCreate],
  );

  usePageNavigation({ breadcrumbs, headerActions });

  // Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Estado para el tab activo de estados
  const [activeTab, setActiveTab] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState<EntryStatus | undefined>(undefined);

  // Hook useQueryParams para sincronizar con URL
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'id' },
    sortDirection: { type: 'string', default: 'desc' },
    status: { type: 'string', default: '' },
  });

  // Estados locales sincronizados con URL
  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );
  const [sorting, setSorting] = useState<any>(() => ({
    field: queryParams.sortBy || 'id',
    direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
  }));

  // Sincronizar estado de status desde URL al montar
  useEffect(() => {
    if (queryParams.status) {
      setSelectedStatus(queryParams.status as EntryStatus);
      // Mapear status a key del tab
      const tabKey = getTabKeyForStatus(queryParams.status as EntryStatus);
      setActiveTab(tabKey);
    }
  }, [queryParams.status]);

  // Efectos para sincronizar estados locales con URL
  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    setFilterConditions(Array.isArray(queryParams.filters) ? queryParams.filters : []);
  }, [queryParams.filters]);

  useEffect(() => {
    const nextSorting = {
      field: queryParams.sortBy || 'id',
      direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
    };
    setSorting((prev: any) => {
      if (prev && prev.field === nextSorting.field && prev.direction === nextSorting.direction) {
        return prev;
      }
      return nextSorting;
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  // Combinar filterConditions con el filtro de status del tab
  const combinedFilterConditions = useMemo(() => {
    if (!selectedStatus) return filterConditions;

    // Agregar filtro de status si hay un status seleccionado
    // Usar operador 'in' con array de valores (requerido por la API)
    // Nota: FilterConditionOutput tiene value como string, pero la API espera array para 'in'
    const statusFilter = {
      column: 'status',
      action: 'in',
      value: [selectedStatus],
    } as unknown as FilterConditionOutput;

    // Verificar si ya hay un filtro de status en filterConditions
    const hasStatusFilter = filterConditions.some((f) => f.column === 'status');
    if (hasStatusFilter) return filterConditions;

    return [...filterConditions, statusFilter];
  }, [filterConditions, selectedStatus]);

  // Handlers optimizados para evitar bucles infinitos
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      if (queryParams.search !== searchValue) {
        updateQueryParams({ search: searchValue });
      }
    },
    [updateQueryParams, queryParams.search],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      const currentFilters = Array.isArray(queryParams.filters) ? queryParams.filters : [];
      if (JSON.stringify(currentFilters) !== JSON.stringify(conditions)) {
        updateQueryParams({ filters: conditions });
      }
    },
    [updateQueryParams, queryParams.filters],
  );

  const handleSortingChange = useCallback(
    (newSorting: any) => {
      setSorting(newSorting);
      const sortBy = newSorting?.field || 'id';
      const sortDirection = newSorting?.direction || 'desc';

      if (queryParams.sortBy !== sortBy || queryParams.sortDirection !== sortDirection) {
        updateQueryParams({
          sortBy: sortBy === 'id' ? undefined : sortBy,
          sortDirection: sortDirection === 'desc' ? undefined : sortDirection,
        });
      }
    },
    [updateQueryParams, queryParams.sortBy, queryParams.sortDirection],
  );

  const handleStatusChange = useCallback(
    (status?: EntryStatus) => {
      setSelectedStatus(status);
      updateQueryParams({ status: status || '' });
    },
    [updateQueryParams],
  );

  // Convertir filterConditions a formato para el hook de contadores
  const advancedFiltersForCounts = useMemo(() => {
    if (!filterConditions || filterConditions.length === 0) {
      return undefined;
    }
    return {
      filters: filterConditions.map((f) => ({
        column: f.column,
        operator: f.action,
        value: f.value,
      })),
      logic: 'AND',
    };
  }, [filterConditions]);

  const productIdsForCounts = useMemo(() => {
    const productCondition = filterConditions.find(
      (f) => (f.column === 'product_ids' || f.column === 'product_id') && f.action === 'in',
    );

    if (!productCondition?.value) {
      return [];
    }

    const values = Array.isArray(productCondition.value) ? productCondition.value : [productCondition.value];

    return values.map(Number).filter((id) => !Number.isNaN(id));
  }, [filterConditions]);

  // Hook para obtener contadores de entries por status
  const { counts: statusCounts, isLoading: isLoadingCounts } = useEntriesCountByStatus({
    search: searchTerm,
    advancedFilters: advancedFiltersForCounts,
    productIds: productIdsForCounts,
  });

  // Hook que maneja toda la lógica de negocio
  const {
    // Datos
    entries,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    entriesViewProps,

    // Handlers
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Estado de filtros
    hasActiveFilters,
  } = useEntriesMain(searchTerm, combinedFilterConditions, sorting);

  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const renderEntries = () => {
    if (isMobileViewport) {
      return (
        <div className="min-h-0 flex-1">
          <EntryCardList
            entries={entries}
            fetchNextPage={handleFetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            total={total}
            isTenant3pl={isTenant3pl}
            onCancelEntry={handleCancelEntry}
            canCancel={canCancel}
          />
        </div>
      );
    }

    // Usar AG Grid para la vista de escritorio
    return (
      <EntryAgGridTable
        {...entriesViewProps}
        fetchNextPage={handleFetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        onCancelEntry={handleCancelEntry}
        canCancel={canCancel}
        isTenant3pl={isTenant3pl}
      />
    );
  };

  // Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return <LoadingState message={t('list.loading', 'Cargando entradas...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col px-6 pt-4 pb-4">
      {/* Bloque superior: filtros + pestañas (no comprime la tabla) */}
      <div className="mb-4 shrink-0 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              filterConditions={filterConditions}
              searchTerm={searchTerm}
              hasTeamData={isTenant3pl}
            />
          </div>
          <div className="flex items-center gap-3">
            <TotalResults total={total} />
          </div>
        </div>

        {!isLoading && (
          <EntryStatusToolbar
            onStatusChange={handleStatusChange}
            statusCounts={statusCounts}
            isLoadingCounts={isLoadingCounts}
            active={activeTab}
            setActive={setActiveTab}
          />
        )}
      </div>

      {/* Tabla: ocupa el alto restante del área principal; el scroll queda dentro del grid */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          className={`flex min-h-0 flex-1 flex-col transition-opacity duration-300 ${
            !isLoading && total > 0
              ? isFetching && !isFetchingNextPage
                ? 'opacity-50 pointer-events-none'
                : 'opacity-100'
              : 'opacity-0 pointer-events-none'
          }`}
        >
          {renderEntries()}
        </div>

        {!isLoading && entries.length === 0 && (
          <div className="absolute inset-0 min-h-[240px]">
            <EmptyView
              type={hasActiveFilters || selectedStatus ? 'noResults' : 'emptyState'}
              icon={EMPTY_VIEW_ICONS.PRODUCTS}
              title={
                hasActiveFilters || selectedStatus
                  ? t('list.noResults.title', 'No se encontraron resultados')
                  : t('list.empty.title', 'No hay entradas')
              }
              description={
                hasActiveFilters || selectedStatus
                  ? t('list.noResults.description', 'Intenta ajustar los filtros de búsqueda')
                  : t('list.empty.description', 'Aún no se han creado entradas de mercancía')
              }
            />
          </div>
        )}
      </div>

      {/* Modal de cancelación de entrada */}
      <CancelEntryModal ref={cancelEntryModalRef} />
    </div>
  );
};

// Helper para mapear status a key del tab
function getTabKeyForStatus(status: EntryStatus): string {
  const map: Record<EntryStatus, string> = {
    PENDIENTE: 'pending',
    EN_RECEPCION: 'in_reception',
    EN_RECEPCION_PARCIAL: 'partial_reception',
    FINALIZADA: 'finished',
    CANCELADA: 'cancelled',
  };
  return map[status] || 'all';
}
